import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, PageSkeleton } from '@daycal/ui';
import { formatters } from '@daycal/shared';
import { api } from '../lib/apiClient.js';
import { BOOKING_BASE_URL } from '../lib/config.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import EventAvailabilityModal from '../components/EventAvailabilityModal.jsx';
import EventAvailabilityFields, { rowsFromRules, rulesFromRows, DEFAULT_AVAIL_ROWS } from '../components/EventAvailabilityFields.jsx';
import {
  FiCalendar, FiFileText, FiExternalLink, FiX, FiCopy, FiCheck, 
  FiPlus, FiClock, FiDollarSign, FiVideo, FiMapPin, FiPhone, 
  FiEdit3, FiTrash2, FiLayers, FiSliders, FiLink, 
  FiGlobe, FiChevronDown, FiZap, FiCheckCircle
} from 'react-icons/fi';
import { LuSparkles } from 'react-icons/lu';

const DURATION_OPTS = [
  { value: 15, label: '15 Mins', icon: FiClock },
  { value: 30, label: '30 Mins', icon: FiClock },
  { value: 45, label: '45 Mins', icon: FiClock },
  { value: 60, label: '60 Mins', icon: FiClock }
];

const BUFFER_OPTS = [
  { value: 0, label: 'No Buffer', icon: FiZap },
  { value: 10, label: '10 Mins Padding', icon: FiClock },
  { value: 15, label: '15 Mins Padding', icon: FiClock },
  { value: 30, label: '30 Mins Padding', icon: FiClock }
];

const ADVANCE_OPTS = [
  { value: 14, label: '14 Days Window', icon: FiCalendar },
  { value: 30, label: '30 Days Window', icon: FiCalendar },
  { value: 60, label: '60 Days Window', icon: FiCalendar },
  { value: 90, label: '90 Days Window', icon: FiCalendar }
];

const LOCATION_OPTS = [
  { value: 'online', label: 'Online (Video Call)', icon: FiVideo },
  { value: 'in_person', label: 'In Person Meeting', icon: FiMapPin },
  { value: 'phone', label: 'Phone Call', icon: FiPhone }
];

const CURRENCY_OPTS = [
  { value: 'INR', label: 'INR (₹)', symbol: '₹' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' }
];

const STATUS_OPTS = [
  { value: 'active', label: 'Active Service' },
  { value: 'inactive', label: 'Inactive / Hidden' }
];

const DEFAULT_FORM = {
  title: '',
  slug: '',
  duration_min: 30,
  buffer_before_min: 0,
  advance_booking_days: 30,
  location_type: 'online',
  description: '',
  require_payment: false,
  price: '',
  currency: 'INR',
  redirect_url: '',
};

export default function EventTypes() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const username = profile?.username || 'user';
  
  // Post-booking redirect (funnel) is a paid capability — only shown when the plan allows it.
  const canRedirect = profile?.allow_event_redirect === true;
  const { data, isLoading } = useQuery({ queryKey: ['event-types'], queryFn: api.listEventTypes });
  // Host default availability — used to seed the inline editor and as the per-event timezone.
  const { data: hostAvail } = useQuery({ queryKey: ['availability'], queryFn: api.getAvailability });
  const hostTz = hostAvail?.schedule?.timezone || 'Asia/Kolkata';
  
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [availFor, setAvailFor] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  // Inline per-event availability state for the create + edit popups.
  const [createCustom, setCreateCustom] = useState(false);
  const [createRows, setCreateRows] = useState(DEFAULT_AVAIL_ROWS);
  const [editCustom, setEditCustom] = useState(false);
  const [editRows, setEditRows] = useState(DEFAULT_AVAIL_ROWS);

  // Seed the create popup's availability editor from the host default when it opens.
  useEffect(() => {
    if (!isCreating) return;
    setCreateCustom(false);
    setCreateRows(hostAvail?.rules ? rowsFromRules(hostAvail.rules) : DEFAULT_AVAIL_ROWS);
  }, [isCreating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load + seed the edit popup's availability from the event being edited.
  const { data: editAvailData } = useQuery({
    queryKey: ['event-availability', editing?.id],
    queryFn: () => api.getEventAvailability(editing.id),
    enabled: !!editing?.id,
  });
  useEffect(() => {
    if (!editAvailData) return;
    setEditCustom(!!editAvailData.custom);
    setEditRows(rowsFromRules(editAvailData.rules));
  }, [editAvailData]);

  const handleTitleChange = (val) => {
    const updated = { ...form, title: val };
    if (!slugManuallyEdited) {
      updated.slug = val
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }
    setForm(updated);
  };

  const handleCopy = (e, url, id) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.createEventType({
        title: form.title,
        slug: form.slug,
        duration_min: Number(form.duration_min),
        buffer_before_min: Number(form.buffer_before_min),
        buffer_after_min: Number(form.buffer_before_min),
        advance_booking_days: Number(form.advance_booking_days),
        location_type: form.location_type,
        description: form.description,
        require_payment: form.require_payment,
        price_minor: form.require_payment ? Math.round(Number(form.price) * 100) : null,
        currency: form.currency,
        redirect_url: form.redirect_url,
      });
      // Apply custom availability to the just-created event (needs its id).
      const newId = res?.event_type?.id;
      if (createCustom && newId) {
        await api.updateEventAvailability(newId, { custom: true, timezone: hostTz, rules: rulesFromRows(createRows) });
      }
      return res;
    },
    onSuccess: () => {
      setForm(DEFAULT_FORM);
      setSlugManuallyEdited(false);
      setError('');
      setIsCreating(false);
      setCreateCustom(false);
      setCreateRows(DEFAULT_AVAIL_ROWS);
      qc.invalidateQueries({ queryKey: ['event-types'] });
    },
    onError: (err) => setError(err.message),
  });

  const remove = useMutation({
    mutationFn: (id) => api.deleteEventType(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-types'] }),
  });

  const update = useMutation({
    mutationFn: async () => {
      await api.updateEventType(editing.id, {
        title: editing.title,
        slug: editing.slug,
        duration_min: Number(editing.duration_min),
        buffer_before_min: Number(editing.buffer_before_min),
        buffer_after_min: Number(editing.buffer_before_min),
        advance_booking_days: Number(editing.advance_booking_days),
        location_type: editing.location_type,
        description: editing.description,
        require_payment: editing.require_payment,
        price_minor: editing.require_payment ? Math.round(Number(editing.price) * 100) : null,
        currency: editing.currency,
        status: editing.status,
        redirect_url: editing.redirect_url,
      });
      // Persist the event's availability (custom hours, or revert to default).
      await api.updateEventAvailability(editing.id, {
        custom: editCustom,
        timezone: editAvailData?.schedule?.timezone || hostTz,
        rules: rulesFromRows(editRows),
      });
    },
    onSuccess: () => {
      setEditing(null);
      setError('');
      qc.invalidateQueries({ queryKey: ['event-types'] });
      qc.invalidateQueries({ queryKey: ['event-availability'] });
    },
    onError: (err) => setError(err.message),
  });

  const openEdit = (et) => {
    setEditing({
      id: et.id,
      title: et.title ?? '',
      slug: et.slug ?? '',
      duration_min: et.duration_min ?? 30,
      buffer_before_min: et.buffer_before_min ?? 0,
      advance_booking_days: et.advance_booking_days ?? 30,
      location_type: et.location_type ?? 'online',
      description: et.description ?? '',
      require_payment: et.require_payment ?? false,
      price: et.price_minor != null ? (et.price_minor / 100).toString() : '',
      currency: et.currency ?? 'INR',
      status: et.status ?? 'active',
      redirect_url: et.redirect_url ?? '',
    });
    setEditCustom(false);
    setEditRows(DEFAULT_AVAIL_ROWS);
    setError('');
  };

  if (isLoading) return <PageSkeleton />;
  const eventTypes = data?.event_types || [];
  const activeCount = eventTypes.filter(e => e.status !== 'inactive').length;
  const paidCount = eventTypes.filter(e => e.require_payment).length;

  const getLocationIcon = (loc) => {
    if (loc === 'in_person') return <FiMapPin className="h-3.5 w-3.5 text-amber-500" />;
    if (loc === 'phone') return <FiPhone className="h-3.5 w-3.5 text-blue-500" />;
    return <FiVideo className="h-3.5 w-3.5 text-indigo-500" />;
  };

  const getLocationLabel = (loc) => {
    if (loc === 'in_person') return 'In Person';
    if (loc === 'phone') return 'Phone Call';
    return 'Video Call';
  };

  return (
    <div className="animate-dc-rise mx-auto max-w-5xl pb-16">
      {/* Header Banner */}
      <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between border-b border-slate-200/70 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-500/20">
              <LuSparkles className="h-3.5 w-3.5 text-indigo-600 animate-pulse" />
              <span>Consultation Services</span>
            </span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Events</h1>
          <p className="mt-1 text-sm text-slate-500">
            Create, manage and configure your appointment services, pricing structures, and scheduling rules.
          </p>

          {/* Quick Stats Pills */}
          <div className="mt-3.5 flex flex-wrap items-center gap-3 text-xs font-medium text-slate-600">
            <div className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 border border-slate-200 shadow-2xs">
              <span className="h-2 w-2 rounded-full bg-indigo-500" />
              <span>Total Services: <strong className="text-slate-900 font-bold">{eventTypes.length}</strong></span>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 border border-slate-200 shadow-2xs">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span>Active: <strong className="text-slate-900 font-bold">{activeCount}</strong></span>
            </div>
            {paidCount > 0 && (
              <div className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1 border border-slate-200 shadow-2xs">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                <span>Paid Services: <strong className="text-slate-900 font-bold">{paidCount}</strong></span>
              </div>
            )}
          </div>
        </div>

        <div>
          <button
            onClick={() => {
              setIsCreating(!isCreating);
              if (!isCreating) {
                setForm(DEFAULT_FORM);
                setSlugManuallyEdited(false);
                setError('');
              }
            }}
            className={`group inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold shadow-md transition-all duration-300 ${
              isCreating 
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 shadow-slate-200/50' 
                : 'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white hover:from-indigo-600 hover:to-violet-600 shadow-indigo-900/20 hover:shadow-indigo-500/25 hover:-translate-y-0.5'
            }`}
          >
            {isCreating ? (
              <>
                <FiX className="h-4 w-4 transition-transform group-hover:rotate-90" />
                <span>Close Form</span>
              </>
            ) : (
              <>
                <FiPlus className="h-4 w-4 transition-transform group-hover:scale-110" />
                <span>New Consultation Service</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Creation Form Card */}
      {isCreating && (
        <div className="mb-10 animate-dc-rise rounded-3xl border border-slate-200/90 bg-white shadow-[0_20px_50px_-15px_rgba(0,0,0,0.08)] transition-all">
          {/* Top Gradient Bar */}
          <div className="h-1.5 rounded-t-3xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
          
          <div className="p-6 md:p-8">
            {/* Header section */}
            <div className="mb-8 flex items-center justify-between border-b border-slate-100 pb-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-500/20">
                  <FiLayers className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-lg font-extrabold text-slate-900">Create a New Consultation Service</h2>
                  <p className="text-xs text-slate-500">Configure your booking parameters, availability rules, and pricing.</p>
                </div>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                Step 1 of 1
              </span>
            </div>

            {/* Section 1: Basic Information */}
            <div className="mb-8 space-y-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-600">
                <FiLayers className="h-3.5 w-3.5" />
                <span>1. Service Details</span>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                {/* Service Title */}
                <div>
                  <label className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-700">
                    <span>Service Title <span className="text-rose-500">*</span></span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form.title}
                      onChange={e => handleTitleChange(e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition-all placeholder:text-slate-400 hover:border-slate-400 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-500/15 shadow-2xs"
                      placeholder="e.g. 1:1 Business Strategy Session"
                    />
                  </div>
                </div>

                {/* URL Slug with Prefix Pill */}
                <div>
                  <label className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-700">
                    <span>URL Slug <span className="text-rose-500">*</span></span>
                    <span className="text-[11px] text-slate-400 font-normal">Auto-generated</span>
                  </label>
                  <div className="flex rounded-2xl border border-slate-300 bg-white overflow-hidden hover:border-slate-400 focus-within:border-indigo-600 focus-within:ring-4 focus-within:ring-indigo-500/15 transition-all shadow-2xs">
                    <span className="flex items-center bg-slate-100/90 px-3.5 text-xs font-semibold text-slate-600 border-r border-slate-300 shrink-0">
                      /u/{username}/
                    </span>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={e => {
                        setForm({...form, slug: e.target.value});
                        setSlugManuallyEdited(true);
                      }}
                      className="w-full bg-transparent px-3.5 py-3 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                      placeholder="strategy-session"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-700">
                  <span>Description & Topics Covered</span>
                  <span className="text-[11px] text-slate-400 font-normal">Shown on booking page</span>
                </label>
                <div className="relative">
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={e => setForm({...form, description: e.target.value})}
                    className="w-full resize-none rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition-all placeholder:text-slate-400 hover:border-slate-400 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-500/15 shadow-2xs"
                    placeholder="Provide a brief summary of what clients can expect during this consultation..."
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Scheduling Rules & Location */}
            <div className="mb-8 space-y-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-600">
                <FiSliders className="h-3.5 w-3.5" />
                <span>2. Scheduling & Location</span>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Duration</label>
                  <CustomSelect
                    options={DURATION_OPTS}
                    value={form.duration_min}
                    onChange={val => setForm({...form, duration_min: val})}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Buffer Padding</label>
                  <CustomSelect
                    options={BUFFER_OPTS}
                    value={form.buffer_before_min}
                    onChange={val => setForm({...form, buffer_before_min: val})}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Advance Window</label>
                  <CustomSelect
                    options={ADVANCE_OPTS}
                    value={form.advance_booking_days}
                    onChange={val => setForm({...form, advance_booking_days: val})}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Meeting Location</label>
                  <CustomSelect
                    options={LOCATION_OPTS}
                    value={form.location_type}
                    onChange={val => setForm({...form, location_type: val})}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Pricing & Advanced Options */}
            <div className="mb-8 space-y-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-600">
                <FiDollarSign className="h-3.5 w-3.5" />
                <span>3. Monetization & Post-Booking</span>
              </div>

              {/* Require Payment Toggle Container */}
              <div className={`rounded-2xl border p-5 transition-all duration-300 ${
                form.require_payment 
                  ? 'border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-500/30' 
                  : 'border-slate-300 bg-slate-50/60 hover:border-slate-400'
              }`}>
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-3.5 cursor-pointer flex-1 select-none pr-4"
                    onClick={() => setForm(f => ({ ...f, require_payment: !f.require_payment }))}
                  >
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors ${
                      form.require_payment ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20' : 'bg-slate-200 text-slate-600'
                    }`}>
                      <FiDollarSign className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-slate-900">Require Payment Before Booking</span>
                        {form.require_payment && (
                          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800 uppercase tracking-wide">
                            Paid Service
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Clients will be required to enter payment details before their appointment is reserved.
                      </p>
                    </div>
                  </div>

                  {/* Clean Accessible Switch Toggle */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.require_payment}
                    onClick={() => setForm(f => ({ ...f, require_payment: !f.require_payment }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                      form.require_payment ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                        form.require_payment ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Price & Currency conditional section */}
                {form.require_payment && (
                  <div className="mt-5 border-t border-emerald-200/80 pt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 animate-dc-rise">
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">Consultation Fee</label>
                      <div className="flex rounded-2xl border border-emerald-400 bg-white overflow-hidden hover:border-emerald-500 focus-within:border-emerald-600 focus-within:ring-4 focus-within:ring-emerald-500/15 transition-all shadow-2xs">
                        <span className="flex items-center bg-emerald-100/80 px-3.5 text-xs font-extrabold text-emerald-900 border-r border-emerald-300 shrink-0">
                          {CURRENCY_OPTS.find(c => c.value === form.currency)?.symbol || '₹'}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.price}
                          onChange={e => setForm({...form, price: e.target.value})}
                          className="w-full bg-transparent px-3.5 py-2.5 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">Currency</label>
                      <CustomSelect
                        options={CURRENCY_OPTS}
                        value={form.currency}
                        onChange={val => setForm({...form, currency: val})}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Redirect URL (paid capability check) */}
              {canRedirect && (
                <div className="rounded-2xl border border-slate-300 bg-slate-50/60 p-5 hover:border-slate-400 transition-all">
                  <label className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-700">
                    <span className="flex items-center gap-1.5">
                      <FiLink className="h-3.5 w-3.5 text-indigo-500" />
                      <span>Custom Thank-You Redirect URL</span>
                      <span className="font-normal text-slate-400 text-[11px]">(Optional)</span>
                    </span>
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700 uppercase">PRO FEATURE</span>
                  </label>
                  <input
                    type="url"
                    value={form.redirect_url}
                    onChange={e => setForm({...form, redirect_url: e.target.value})}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-900 outline-none hover:border-slate-400 focus:border-indigo-600 focus:ring-4 focus:ring-indigo-500/15 shadow-2xs placeholder:text-slate-400"
                    placeholder="https://yoursite.com/thank-you"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    After a booking is confirmed, guests are redirected to this URL instead of the default confirmation page.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-xs font-semibold text-rose-700 flex items-center gap-2">
                <FiX className="h-4 w-4 shrink-0 text-rose-500" />
                <span>{error}</span>
              </div>
            )}

            {/* Per-event availability */}
            <div className="mb-6 border-t border-slate-100 pt-6">
              <h3 className="mb-1 text-sm font-bold text-slate-900">Availability</h3>
              <p className="mb-3 text-xs text-slate-500">Choose when this event can be booked.</p>
              <EventAvailabilityFields custom={createCustom} setCustom={setCreateCustom} rows={createRows} setRows={setCreateRows} />
            </div>

            {/* Form Action Buttons */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-2xs transition-colors hover:bg-slate-100 hover:text-slate-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => create.mutate()}
                disabled={create.isPending || !form.title || !form.slug || (form.require_payment && (!form.price || Number(form.price) <= 0))}
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 px-7 py-2.5 text-sm font-semibold text-white shadow-md shadow-slate-900/15 transition-all hover:from-indigo-600 hover:to-violet-600 hover:shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {create.isPending ? <Spinner /> : (
                  <>
                    <FiCheckCircle className="h-4 w-4 text-emerald-400" />
                    <span>Create Consultation Service</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cards List Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {eventTypes.map((et) => {
          const isInactive = et.status === 'inactive';
          return (
            <div
              key={et.id}
              className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-7 shadow-xs transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(79,70,229,0.1)] hover:border-indigo-200/80 ${
                isInactive ? 'opacity-75 bg-slate-50/50' : ''
              }`}
            >
              {/* Top Accent Gradient Line */}
              <div className={`absolute inset-x-0 top-0 h-1.5 transition-opacity ${
                isInactive 
                  ? 'bg-slate-300' 
                  : et.require_payment 
                    ? 'bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500' 
                    : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500'
              }`} />

              <div>
                {/* Header Pills Row */}
                <div className="mb-4 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-white shadow-xs">
                      <FiClock className="h-3 w-3 text-indigo-300" />
                      <span>{et.duration_min} Mins</span>
                    </span>

                    {et.require_payment ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-emerald-800">
                        <FiDollarSign className="h-3 w-3 text-emerald-600" />
                        <span>{formatters.formatMoney(et.price_minor, et.currency)}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider text-slate-600">
                        <span>FREE</span>
                      </span>
                    )}
                  </div>

                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                    isInactive ? 'bg-slate-200 text-slate-600' : 'bg-emerald-100 text-emerald-800'
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${isInactive ? 'bg-slate-500' : 'bg-emerald-500'}`} />
                    <span>{isInactive ? 'Inactive' : 'Active'}</span>
                  </span>
                </div>

                {/* Service Title */}
                <h3 className="text-xl font-bold tracking-tight text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                  {et.title}
                </h3>

                {/* Slug display */}
                <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-slate-400">
                  <FiGlobe className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="truncate">/u/{username}/{et.slug}</span>
                </div>

                {/* Description */}
                <p className="mb-6 mt-3 min-h-[42px] text-xs leading-relaxed text-slate-500 line-clamp-2">
                  {et.description || `Standard ${et.duration_min}-minute consultation booking session.`}
                </p>

                {/* Meta details chips */}
                <div className="mb-6 flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100/80 px-3 py-1.5 border border-slate-200/60 text-[11px] font-semibold text-slate-700">
                    {getLocationIcon(et.location_type)}
                    <span>{getLocationLabel(et.location_type)}</span>
                  </div>

                  <div className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100/80 px-3 py-1.5 border border-slate-200/60 text-[11px] font-semibold text-slate-700">
                    <FiCalendar className="h-3.5 w-3.5 text-indigo-500" />
                    <span>{et.advance_booking_days || 30}d Window</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate(`/form?eventTypeId=${et.id}`)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 px-3 py-1.5 border border-indigo-200/70 text-[11px] font-bold text-indigo-700 transition-all hover:bg-indigo-100 hover:border-indigo-300"
                  >
                    <FiFileText className="h-3.5 w-3.5 text-indigo-600" />
                    <span>Form Questions &rarr;</span>
                  </button>
                </div>
              </div>

              {/* Bottom Actions Footer */}
              <div className="flex items-center justify-between border-t border-slate-100 pt-5 mt-auto">
                <div className="flex items-center gap-2">
                  <a
                    href={`${BOOKING_BASE_URL}/u/${username}/${et.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800 transition-colors hover:text-indigo-600"
                  >
                    <span>Booking Page</span>
                    <FiExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                  </a>

                  <button
                    type="button"
                    onClick={(e) => handleCopy(e, `${BOOKING_BASE_URL}/u/${username}/${et.slug}`, et.id)}
                    title="Copy booking URL"
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-200 hover:text-slate-900 border border-slate-200"
                  >
                    {copiedId === et.id ? (
                      <>
                        <FiCheck className="h-3.5 w-3.5 text-emerald-600" />
                        <span className="text-emerald-700 font-bold">Copied</span>
                      </>
                    ) : (
                      <>
                        <FiCopy className="h-3.5 w-3.5 text-slate-400" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAvailFor(et)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-700 shadow-2xs transition-all hover:bg-slate-100 hover:text-slate-900"
                  >
                    <FiClock className="h-3.5 w-3.5 text-slate-500" />
                    <span>Availability</span>
                  </button>
                  <button
                    onClick={() => openEdit(et)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-700 shadow-2xs transition-all hover:bg-slate-100 hover:text-slate-900"
                  >
                    <FiEdit3 className="h-3.5 w-3.5 text-slate-500" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => setConfirmDelete(et)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3.5 py-1.5 text-xs font-bold text-rose-600 transition-all hover:bg-rose-100 hover:text-rose-700"
                  >
                    <FiTrash2 className="h-3.5 w-3.5 text-rose-500" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {eventTypes.length === 0 && !isCreating && (
          <div className="col-span-full rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-2xs">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-500/20">
              <FiLayers className="h-7 w-7" />
            </div>
            <h3 className="text-base font-bold text-slate-900">No consultation services created yet</h3>
            <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
              Create your first consultation service to start accepting automated bookings and payments.
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-xs font-semibold text-white hover:bg-indigo-600 transition-colors"
            >
              <FiPlus className="h-4 w-4" />
              <span>Create First Service</span>
            </button>
          </div>
        )}
      </div>

      {/* Edit Service Modal */}
      {editing && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-md transition-all"
          onClick={() => setEditing(null)}
        >
          <div
            className="my-8 w-full max-w-3xl rounded-3xl bg-white shadow-2xl animate-dc-rise border border-slate-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 md:px-8 bg-slate-50/50">
              <div className="flex items-center gap-2">
                <FiEdit3 className="h-4 w-4 text-indigo-600" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">Edit Consultation Service</h2>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6 md:px-8 max-h-[75vh] overflow-y-auto custom-scrollbar">
              <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Service Title</label>
                  <input
                    value={editing.title}
                    onChange={e => setEditing({...editing, title: e.target.value})}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">URL Slug</label>
                  <input
                    value={editing.slug}
                    onChange={e => setEditing({...editing, slug: e.target.value})}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:bg-white"
                  />
                </div>
              </div>

              <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Duration</label>
                  <CustomSelect
                    options={DURATION_OPTS}
                    value={editing.duration_min}
                    onChange={val => setEditing({...editing, duration_min: val})}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Buffer Padding</label>
                  <CustomSelect
                    options={BUFFER_OPTS}
                    value={editing.buffer_before_min}
                    onChange={val => setEditing({...editing, buffer_before_min: val})}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Advance Window</label>
                  <CustomSelect
                    options={ADVANCE_OPTS}
                    value={editing.advance_booking_days}
                    onChange={val => setEditing({...editing, advance_booking_days: val})}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Meeting Location</label>
                  <CustomSelect
                    options={LOCATION_OPTS}
                    value={editing.location_type}
                    onChange={val => setEditing({...editing, location_type: val})}
                  />
                </div>
              </div>

              <div className="mb-5">
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">Description</label>
                <textarea
                  rows={3}
                  value={editing.description}
                  onChange={e => setEditing({...editing, description: e.target.value})}
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:bg-white"
                />
              </div>

              {canRedirect && (
                <div className="mb-5">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Redirect URL <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={editing.redirect_url}
                    onChange={e => setEditing({...editing, redirect_url: e.target.value})}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:bg-white"
                    placeholder="https://yoursite.com/thank-you"
                  />
                </div>
              )}

              {/* Require Payment Toggle in Edit Modal */}
              <div className={`mb-5 rounded-2xl border p-5 transition-all duration-300 ${
                editing.require_payment 
                  ? 'border-emerald-300/90 bg-emerald-50/40 ring-1 ring-emerald-500/20' 
                  : 'border-slate-200 bg-slate-50/50 hover:bg-slate-50'
              }`}>
                <div className="flex items-center justify-between">
                  <div 
                    className="flex items-center gap-3.5 cursor-pointer flex-1 select-none pr-4"
                    onClick={() => setEditing(e => ({ ...e, require_payment: !e.require_payment }))}
                  >
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                      editing.require_payment ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20' : 'bg-slate-200 text-slate-600'
                    }`}>
                      <FiDollarSign className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-slate-900">Require Payment</span>
                        {editing.require_payment && (
                          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800 uppercase tracking-wide">
                            Paid Service
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">Prompt clients to complete payment before booking.</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={editing.require_payment}
                    onClick={() => setEditing(e => ({ ...e, require_payment: !e.require_payment }))}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                      editing.require_payment ? 'bg-emerald-600' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                        editing.require_payment ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                {editing.require_payment && (
                  <>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">Consultation Fee</label>
                      <div className="flex rounded-2xl border border-emerald-300 bg-white overflow-hidden focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10 transition-all">
                        <span className="flex items-center bg-emerald-50 px-3 text-xs font-bold text-emerald-800 border-r border-emerald-200 shrink-0">
                          {CURRENCY_OPTS.find(c => c.value === editing.currency)?.symbol || '₹'}
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editing.price}
                          onChange={e => setEditing({...editing, price: e.target.value})}
                          className="w-full bg-transparent px-3 py-2.5 text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-400"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">Currency</label>
                      <CustomSelect
                        options={CURRENCY_OPTS}
                        value={editing.currency}
                        onChange={val => setEditing({...editing, currency: val})}
                      />
                    </div>
                  </>
                )}
                <div className={editing.require_payment ? "" : "md:col-start-1"}>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-700">Status</label>
                  <CustomSelect
                    options={STATUS_OPTS}
                    value={editing.status}
                    onChange={val => setEditing({...editing, status: val})}
                  />
                </div>
              </div>

              {error && <p className="mt-4 text-xs font-semibold text-rose-600">{error}</p>}

              {/* Per-event availability */}
              <div className="mt-6 border-t border-slate-100 pt-6">
                <h3 className="mb-1 text-sm font-bold text-slate-900">Availability</h3>
                <p className="mb-3 text-xs text-slate-500">Choose when this event can be booked.</p>
                <EventAvailabilityFields custom={editCustom} setCustom={setEditCustom} rows={editRows} setRows={setEditRows} />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4 md:px-8 bg-slate-50/50">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-full bg-white px-6 py-2.5 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors shadow-2xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => update.mutate()}
                disabled={update.isPending || !editing.title || !editing.slug}
                className="rounded-full bg-slate-900 px-6 py-2.5 text-xs font-bold text-white hover:bg-indigo-600 transition-colors disabled:opacity-50"
              >
                {update.isPending ? <Spinner /> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-md transition-all"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-7 shadow-2xl animate-dc-rise text-center border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-rose-500/20">
              <FiTrash2 className="h-6 w-6" />
            </div>
            <h3 className="mb-1 text-lg font-bold text-slate-900">Delete Event Type?</h3>
            <p className="mb-6 text-xs text-slate-500 leading-relaxed">
              Are you sure you want to delete <strong className="text-slate-800">{confirmDelete.title}</strong>? Existing bookings will not be deleted, but clients won't be able to book this service.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="flex-1 rounded-full bg-slate-100 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  remove.mutate(confirmDelete.id);
                  setConfirmDelete(null);
                }}
                disabled={remove.isPending}
                className="flex-1 rounded-full bg-rose-600 py-2.5 text-xs font-bold text-white hover:bg-rose-700 transition-colors disabled:opacity-50"
              >
                {remove.isPending ? <Spinner /> : 'Delete Service'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {availFor && <EventAvailabilityModal event={availFor} onClose={() => setAvailFor(null)} />}
    </div>
  );
}

function CustomSelect({ options, value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selectedOpt = options.find(o => String(o.value) === String(value));
  const selectedLabel = selectedOpt?.label || placeholder || 'Select...';
  const Icon = selectedOpt?.icon;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={`w-full h-11 flex items-center justify-between rounded-2xl border px-3.5 text-xs font-semibold outline-none transition-all shadow-2xs ${
          disabled 
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-300' 
            : open 
              ? 'bg-white text-slate-900 border-indigo-600 ring-4 ring-indigo-500/15' 
              : 'bg-white text-slate-800 border-slate-300 hover:border-slate-400 hover:bg-slate-50/50'
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {Icon && <Icon className="h-4 w-4 text-indigo-500 shrink-0" />}
          <span className="truncate">{selectedLabel}</span>
        </span>
        <FiChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 shrink-0 ${open ? 'rotate-180 text-indigo-600' : ''}`} />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 z-50 mt-1.5 rounded-2xl border border-slate-200/90 bg-white py-1.5 shadow-xl animate-dc-rise max-h-60 overflow-y-auto custom-scrollbar">
          {options.map((opt) => {
            const OptIcon = opt.icon;
            const isSelected = String(value) === String(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`w-full px-3.5 py-2.5 text-left text-xs transition-colors flex items-center justify-between ${
                  isSelected
                    ? 'bg-indigo-50/80 font-bold text-indigo-950'
                    : 'text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <span className="flex items-center gap-2 truncate">
                  {OptIcon && <OptIcon className={`h-4 w-4 shrink-0 ${isSelected ? 'text-indigo-600' : 'text-slate-400'}`} />}
                  <span className="truncate">{opt.label}</span>
                </span>
                {isSelected && <FiCheck className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}